import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatearFecha, formatearFechaHora, type EstadoOT } from '@cmms/shared';
import { api, consulta } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import {
  Cargando,
  Dialogo,
  ErroresFormulario,
  EstadoOtEtiqueta,
  GaleriaImagenes,
  Indicador,
  SubidaImagenes,
  Vacio,
  type AdjuntoImagen,
  type Tono,
} from '../componentes/piezas.js';
import { IconoLupa, IconoOT } from '../componentes/iconos.js';
import {
  SelectorActivo,
  SelectorFecha,
  SelectorLista,
  SelectorUbicacion,
  useUbicaciones,
} from '../componentes/selectores.js';
import { useEsMovil } from '../servicios/responsive.js';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';

interface ObservacionActivoPendiente {
  id: string;
  activoId: string;
  activoNumero: number;
  activoNombre: string;
  grupoNombre: string | null;
  ordenTrabajoNumero: number | null;
  texto: string;
  fechaVencimiento: string | null;
  vencida: boolean;
  resuelta: boolean;
  resueltaEl: string | null;
  ordenTrabajoGeneradaId: string | null;
  ordenTrabajoGeneradaNumero: number | null;
  ordenTrabajoGeneradaEstado: EstadoOT | null;
  creadoPor: string | null;
  creadoEl: string;
}

interface ObservacionUbicacionPendiente {
  id: string;
  ubicacionId: string;
  ubicacionNombre: string;
  grupoNombre: string | null;
  ordenTrabajoNumero: number | null;
  texto: string;
  fechaVencimiento: string | null;
  vencida: boolean;
  resuelta: boolean;
  resueltaEl: string | null;
  ordenTrabajoGeneradaId: string | null;
  ordenTrabajoGeneradaNumero: number | null;
  ordenTrabajoGeneradaEstado: EstadoOT | null;
  creadoPor: string | null;
  creadoEl: string;
}

interface ObservacionSujetoPendiente {
  id: string;
  sujetoEvaluableId: string;
  sujetoNombre: string;
  grupoNombre: string | null;
  ordenTrabajoNumero: number | null;
  texto: string;
  fechaVencimiento: string | null;
  vencida: boolean;
  resuelta: boolean;
  resueltaEl: string | null;
  ordenTrabajoGeneradaId: string | null;
  ordenTrabajoGeneradaNumero: number | null;
  ordenTrabajoGeneradaEstado: EstadoOT | null;
  creadoPor: string | null;
  creadoEl: string;
}

/** Forma común para renderizar activos, ubicaciones y personas en la misma cola, sin duplicar la tabla/tarjetas. */
interface ObservacionUnificada {
  id: string;
  entidad: 'ACTIVO' | 'UBICACION' | 'SUJETO';
  entidadId: string;
  etiqueta: string;
  grupoNombre: string | null;
  ordenTrabajoNumero: number | null;
  texto: string;
  fechaVencimiento: string | null;
  vencida: boolean;
  resuelta: boolean;
  ordenTrabajoGeneradaId: string | null;
  ordenTrabajoGeneradaNumero: number | null;
  ordenTrabajoGeneradaEstado: EstadoOT | null;
  creadoPor: string | null;
  creadoEl: string;
}

const ESTADOS = [
  { id: 'PENDIENTE', nombre: 'Pendientes' },
  { id: 'RESUELTA', nombre: 'Cerradas' },
  { id: 'TODAS', nombre: 'Todas' },
];

const TONO_ESTADO_ALERTA: Record<string, Tono> = {
  PENDIENTE: 'ambar',
  RESUELTA: 'verde',
  TODAS: 'azul',
};

/** Ruta de la ficha y parámetros de la OT de seguimiento, según el tipo de entidad. */
function rutaFicha(obs: ObservacionUnificada): string {
  if (obs.entidad === 'SUJETO') return `/personal/${obs.entidadId}`;
  return obs.entidad === 'ACTIVO' ? `/activos/${obs.entidadId}` : `/ubicaciones/${obs.entidadId}`;
}

function etiquetaTipo(entidad: ObservacionUnificada['entidad']): string {
  if (entidad === 'SUJETO') return 'Persona';
  return entidad === 'ACTIVO' ? 'Activo' : 'Ubicación';
}

function paramsNuevaOt(obs: ObservacionUnificada): string {
  const campo = obs.entidad === 'ACTIVO' ? 'activoId' : obs.entidad === 'SUJETO' ? 'sujetoEvaluableId' : 'ubicacionId';
  return `${campo}=${obs.entidadId}&observacionId=${obs.id}&titulo=${encodeURIComponent(`Seguimiento: ${obs.texto.slice(0, 80)}`)}`;
}

/**
 * Cola de "deuda técnica" de activos y ubicaciones (§16.3): cada observación
 * que quedó pendiente al cerrar una OT, en un solo lugar, en vez de tener que
 * entrar entidad por entidad para enterarse de qué falta. Desde acá también
 * se puede abrir directamente la OT de seguimiento cuando corresponde.
 */
/**
 * Alta de una observación fuera de una OT. La ubicación es obligatoria; el
 * activo es opcional. Igual que en la OT: si se elige activo, la observación
 * cae en el activo; si no, en la ubicación.
 */
function ModalNuevaObservacion({ alCerrar }: { alCerrar: () => void }): JSX.Element {
  const cliente = useQueryClient();
  const { data: ubicaciones } = useUbicaciones();
  const [ubicacionId, setUbicacionId] = useState('');
  const [activoId, setActivoId] = useState('');
  const [texto, setTexto] = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [fotos, setFotos] = useState<AdjuntoImagen[]>([]);

  const puedeGuardar = Boolean(ubicacionId) && texto.trim().length > 0;

  const registrar = useMutation({
    mutationFn: () =>
      api.post(
        activoId ? `/activos/${activoId}/observaciones` : `/ubicaciones/${ubicacionId}/observaciones`,
        {
          texto: texto.trim(),
          fechaVencimiento: fechaVencimiento || null,
          adjuntos: fotos.map((f) => f.id),
        },
      ),
    onSuccess: async () => {
      await Promise.all([
        cliente.invalidateQueries({ queryKey: ['observaciones-activo-pendientes'] }),
        cliente.invalidateQueries({ queryKey: ['observaciones-ubicacion-pendientes'] }),
      ]);
      alCerrar();
    },
  });

  return (
    <Dialogo titulo="Nueva observación" alCerrar={alCerrar}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (puedeGuardar && !registrar.isPending) registrar.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <SelectorUbicacion
          id="nueva-obs-ubicacion"
          etiqueta="Ubicación"
          valor={ubicacionId}
          alCambiar={(v) => {
            setUbicacionId(v);
            setActivoId('');
          }}
          ubicaciones={ubicaciones}
          requerido
        />

        <SelectorActivo
          id="nueva-obs-activo"
          etiqueta="Activo (opcional)"
          valor={activoId}
          alCambiar={setActivoId}
          ubicacionId={ubicacionId}
        />

        <div className="campo">
          <label htmlFor="nueva-obs-texto">Observación</label>
          <textarea
            id="nueva-obs-texto"
            rows={4}
            maxLength={4000}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </div>

        <SelectorFecha
          id="nueva-obs-vence"
          etiqueta="Atender antes del (opcional)"
          valor={fechaVencimiento}
          alCambiar={setFechaVencimiento}
        />

        <SubidaImagenes etiqueta="Fotos (opcional)" valor={fotos} alCambiar={setFotos} />

        {registrar.isError ? <ErroresFormulario error={registrar.error} /> : null}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button type="submit" className="boton" disabled={!puedeGuardar || registrar.isPending}>
            Guardar observación
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

export function AlertasActivos(): JSX.Element {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const esMovil = useEsMovil();
  const [estado, setEstado] = useState('PENDIENTE');
  // Una observación puede tener varios párrafos (§ pedido: se veía
  // desordenado en la tarjeta); se corta a un tamaño de lectura rápida y
  // «Ver más» la despliega entera, una por una en vez de todas a la vez.
  const [textosExpandidos, setTextosExpandidos] = useState<Set<string>>(new Set());
  const LARGO_MAXIMO_TEXTO = 90;
  const [buscar, setBuscar] = useState('');
  const [soloVencidas, setSoloVencidas] = useState(false);
  const [creando, setCreando] = useState(false);
  // Se trae todo una sola vez (mismo patrón que el resumen de OT y Activos:
  // §badges) y los contadores por estado se calculan del lado del cliente, así
  // cada badge queda seleccionable de forma independiente en vez de solo
  // reflejar el estado que ya trae el desplegable.
  const { data: deActivos, isLoading: cargandoActivos } = useQuery({
    queryKey: ['observaciones-activo-pendientes'],
    queryFn: () => api.get<ObservacionActivoPendiente[]>(`/activos/observaciones/pendientes${consulta({ estado: 'TODAS' })}`),
    enabled: puede('activo:leer'),
  });

  const { data: deUbicaciones, isLoading: cargandoUbicaciones } = useQuery({
    queryKey: ['observaciones-ubicacion-pendientes'],
    queryFn: () =>
      api.get<ObservacionUbicacionPendiente[]>(`/ubicaciones/observaciones/pendientes${consulta({ estado: 'TODAS' })}`),
    enabled: puede('ubicacion:leer'),
  });

  const { data: dePersonas, isLoading: cargandoPersonas } = useQuery({
    queryKey: ['observaciones-sujeto-pendientes'],
    queryFn: () =>
      api.get<ObservacionSujetoPendiente[]>(`/sujetos-evaluables/observaciones/pendientes${consulta({ estado: 'TODAS' })}`),
    enabled: puede('sujeto_evaluable:leer'),
  });

  const isLoading = cargandoActivos || cargandoUbicaciones || cargandoPersonas;

  const data: ObservacionUnificada[] = [
    ...(deActivos ?? []).map(
      (o): ObservacionUnificada => ({
        id: o.id,
        entidad: 'ACTIVO',
        entidadId: o.activoId,
        etiqueta: `#${o.activoNumero} ${o.activoNombre}`,
        grupoNombre: o.grupoNombre,
        ordenTrabajoNumero: o.ordenTrabajoNumero,
        texto: o.texto,
        fechaVencimiento: o.fechaVencimiento,
        vencida: o.vencida,
        resuelta: o.resuelta,
        ordenTrabajoGeneradaId: o.ordenTrabajoGeneradaId,
        ordenTrabajoGeneradaNumero: o.ordenTrabajoGeneradaNumero,
        ordenTrabajoGeneradaEstado: o.ordenTrabajoGeneradaEstado,
        creadoPor: o.creadoPor,
        creadoEl: o.creadoEl,
      }),
    ),
    ...(deUbicaciones ?? []).map(
      (o): ObservacionUnificada => ({
        id: o.id,
        entidad: 'UBICACION',
        entidadId: o.ubicacionId,
        etiqueta: o.ubicacionNombre,
        grupoNombre: o.grupoNombre,
        ordenTrabajoNumero: o.ordenTrabajoNumero,
        texto: o.texto,
        fechaVencimiento: o.fechaVencimiento,
        vencida: o.vencida,
        resuelta: o.resuelta,
        ordenTrabajoGeneradaId: o.ordenTrabajoGeneradaId,
        ordenTrabajoGeneradaNumero: o.ordenTrabajoGeneradaNumero,
        ordenTrabajoGeneradaEstado: o.ordenTrabajoGeneradaEstado,
        creadoPor: o.creadoPor,
        creadoEl: o.creadoEl,
      }),
    ),
    ...(dePersonas ?? []).map(
      (o): ObservacionUnificada => ({
        id: o.id,
        entidad: 'SUJETO',
        entidadId: o.sujetoEvaluableId,
        etiqueta: o.sujetoNombre,
        grupoNombre: o.grupoNombre,
        ordenTrabajoNumero: o.ordenTrabajoNumero,
        texto: o.texto,
        fechaVencimiento: o.fechaVencimiento,
        vencida: o.vencida,
        resuelta: o.resuelta,
        ordenTrabajoGeneradaId: o.ordenTrabajoGeneradaId,
        ordenTrabajoGeneradaNumero: o.ordenTrabajoGeneradaNumero,
        ordenTrabajoGeneradaEstado: o.ordenTrabajoGeneradaEstado,
        creadoPor: o.creadoPor,
        creadoEl: o.creadoEl,
      }),
    ),
  ];

  const porEstado = data.filter((o) => (estado === 'TODAS' ? true : estado === 'PENDIENTE' ? !o.resuelta : o.resuelta));

  const filtroTexto = buscar.trim().toLowerCase();
  const filtradas = porEstado.filter(
    (o) =>
      (!filtroTexto || o.etiqueta.toLowerCase().includes(filtroTexto) || o.texto.toLowerCase().includes(filtroTexto)) &&
      (!soloVencidas || o.vencida),
  );
  const cantidadPorEstado = (e: string): number =>
    data.filter((o) => (e === 'TODAS' ? true : e === 'PENDIENTE' ? !o.resuelta : o.resuelta)).length;
  const vencidas = data.filter((o) => o.vencida).length;

  return (
    <>
      <div className="cabecera-pagina cabecera-lista">
        <div className="cabecera-lista-titulo">
          <h1>Observaciones</h1>
        </div>
        <div className="campo cabecera-lista-buscar">
          <IconoLupa />
          <input
            aria-label="Buscar observaciones"
            placeholder="Buscar observaciones"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
          />
        </div>
        {puede('ot:cambiar_estado') ? (
          <button type="button" className="boton" onClick={() => setCreando(true)}>
            + Nueva observación
          </button>
        ) : null}
      </div>

      {creando ? <ModalNuevaObservacion alCerrar={() => setCreando(false)} /> : null}

      <div className="rejilla-indicadores">
        {ESTADOS.map((e) => (
          <Indicador
            key={e.id}
            titulo={e.nombre.toUpperCase()}
            valor={cantidadPorEstado(e.id)}
            tono={TONO_ESTADO_ALERTA[e.id]}
            activo={estado === e.id}
            className={e.id !== 'PENDIENTE' ? 'col-ocultar-movil' : undefined}
            onClick={() => setEstado(e.id)}
          />
        ))}
        <Indicador
          titulo="VENCIDAS"
          valor={vencidas}
          tono="roja"
          nota={vencidas > 0 ? 'Requieren atención' : 'Al día'}
          activo={soloVencidas}
          onClick={() => setSoloVencidas((v) => !v)}
        />
      </div>

      <div className="filtros filtros-escritorio">
        <SelectorLista id="alertas-estado" etiqueta="Estado" valor={estado} alCambiar={setEstado} opciones={ESTADOS} requerido />
      </div>

      <div className="panel">
        {isLoading ? <Cargando /> : null}
        {filtradas.length === 0 ? (
          <Vacio titulo="Sin observaciones" detalle="No hay deuda técnica registrada con este filtro." />
        ) : null}

        {filtradas.length > 0 ? (
          esMovil ? (
            <div className="tarjetas-ot">
              {filtradas.map((obs) => (
                <div
                  key={`${obs.entidad}-${obs.id}`}
                  className="tarjeta-ot"
                  role="button"
                  tabIndex={0}
                  onClick={() => navegar(rutaFicha(obs))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navegar(rutaFicha(obs));
                  }}
                >
                  <div className="tarjeta-ot-cuerpo">
                    <div className="tarjeta-ot-cabecera">
                      <div className="tarjeta-ot-titulo">{obs.etiqueta}</div>
                      {obs.ordenTrabajoGeneradaId && obs.ordenTrabajoGeneradaEstado ? (
                        <EstadoOtEtiqueta estado={obs.ordenTrabajoGeneradaEstado} />
                      ) : (
                        <span style={{ color: 'var(--texto-medio)', fontSize: 12, fontWeight: 600 }}>Pendiente</span>
                      )}
                    </div>
                    <div className="tarjeta-ot-nota">
                      {obs.texto.length <= LARGO_MAXIMO_TEXTO || textosExpandidos.has(obs.id)
                        ? obs.texto
                        : `${obs.texto.slice(0, LARGO_MAXIMO_TEXTO).trimEnd()}…`}
                      {obs.texto.length > LARGO_MAXIMO_TEXTO ? (
                        <button
                          type="button"
                          className="tarjeta-ot-subordenes"
                          style={{ display: 'inline', marginLeft: 6 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTextosExpandidos((s) => {
                              const copia = new Set(s);
                              if (copia.has(obs.id)) copia.delete(obs.id);
                              else copia.add(obs.id);
                              return copia;
                            });
                          }}
                        >
                          {textosExpandidos.has(obs.id) ? 'Ver menos' : 'Ver más'}
                        </button>
                      ) : null}
                    </div>
                    <div className="tarjeta-ot-datos">
                      <span>{etiquetaTipo(obs.entidad)}</span>
                      <span>Hecho por {obs.creadoPor ?? 'Sistema'} · {formatearFechaHora(obs.creadoEl)}</span>
                      <span>Grupo: {obs.grupoNombre ?? 'Sin grupo'}</span>
                      {obs.ordenTrabajoNumero ? <span>OT #{obs.ordenTrabajoNumero}</span> : null}
                      {obs.fechaVencimiento ? (
                        <span style={obs.vencida ? { color: 'var(--falla)', fontWeight: 700 } : undefined}>
                          {obs.vencida ? 'Venció el ' : ''}
                          {formatearFecha(obs.fechaVencimiento)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {obs.ordenTrabajoGeneradaId ? (
                    <EnlaceRuta
                      to={`/ordenes/${obs.ordenTrabajoGeneradaId}`}
                      className="boton-icono"
                      title="Ver OT de seguimiento"
                      aria-label="Ver OT de seguimiento"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconoOT />
                    </EnlaceRuta>
                  ) : puede('ot:crear') ? (
                    <EnlaceRuta
                      to={`/ordenes/nueva?${paramsNuevaOt(obs)}`}
                      className="boton-icono"
                      title="Crear OT de seguimiento"
                      aria-label="Crear OT de seguimiento"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconoOT />
                    </EnlaceRuta>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Entidad</th>
                    <th>Observación</th>
                    <th>Grupo</th>
                    <th>Origen</th>
                    <th>Estado</th>
                    <th>Vencimiento</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map((obs) => (
                    <tr key={`${obs.entidad}-${obs.id}`} className="fila-clicable" onClick={() => navegar(rutaFicha(obs))}>
                      <td>{etiquetaTipo(obs.entidad)}</td>
                      <td style={{ fontWeight: 600 }}>{obs.etiqueta}</td>
                      <td style={{ maxWidth: 320 }}>
                        <div>{obs.texto}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>
                          Hecho por {obs.creadoPor ?? 'Sistema'} · {formatearFechaHora(obs.creadoEl)}
                        </div>
                      </td>
                      <td>{obs.grupoNombre ?? 'Sin grupo'}</td>
                      <td>{obs.ordenTrabajoNumero ? `OT #${obs.ordenTrabajoNumero}` : '—'}</td>
                      <td>
                        {obs.ordenTrabajoGeneradaId && obs.ordenTrabajoGeneradaEstado ? (
                          <EstadoOtEtiqueta estado={obs.ordenTrabajoGeneradaEstado} />
                        ) : (
                          <span style={{ color: 'var(--texto-medio)' }}>Pendiente</span>
                        )}
                      </td>
                      <td style={obs.vencida ? { color: 'var(--falla)', fontWeight: 700 } : undefined}>
                        {obs.fechaVencimiento ? (
                          <>
                            {obs.vencida ? 'Venció el ' : ''}
                            {formatearFecha(obs.fechaVencimiento)}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="acciones-fila">
                          {obs.ordenTrabajoGeneradaId ? (
                            <EnlaceRuta
                              to={`/ordenes/${obs.ordenTrabajoGeneradaId}`}
                              className="boton-icono"
                              title="Ver OT de seguimiento"
                              aria-label="Ver OT de seguimiento"
                            >
                              <IconoOT />
                            </EnlaceRuta>
                          ) : puede('ot:crear') ? (
                            <EnlaceRuta
                              to={`/ordenes/nueva?${paramsNuevaOt(obs)}`}
                              className="boton-icono"
                              title="Crear OT de seguimiento"
                              aria-label="Crear OT de seguimiento"
                            >
                              <IconoOT />
                            </EnlaceRuta>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </div>
    </>
  );
}
